import type { TFunction } from 'i18next';
import {
  Activity,
  Briefcase,
  CalendarDays,
  Check,
  ChevronRight,
  Circle,
  FileText,
  FilterX,
  GitBranch,
  History,
  Info,
  Layers,
  type LucideIcon,
  Plug,
  Settings2,
  Target,
  TriangleAlert,
  User,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { PLATFORM_LOGO } from '~/components/common/chips/platform-logo';
import { CopyIconButton } from '~/components/common/copy-icon-button';
import { formatStageCode } from '~/components/common/live-stage-steps/format-stage-code';
import { Button } from '~/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { StatusChip } from '~/features/catalog-operations/components/chips';
import { useBatchHeader } from '~/features/catalog-operations/hooks/use-batch-detail';
import { toOptions } from '~/features/catalog-operations/hooks/use-language-approval';
import { useRunTranslations } from '~/features/catalog-operations/hooks/use-translations';
import type { BatchHeader } from '~/features/catalog-operations/types/catalog-operations.types';
import { lastActionLabelKey } from '~/features/catalog-operations/utils/last-action';
import { ProductClassesBreakdown } from '~/features/product-groups/components/product-classes-breakdown';
import { productClassesTotal } from '~/features/product-groups/utils/product-groups';
import { useCanChooseRetailerPrompt } from '~/hooks/useCanChooseRetailerPrompt';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { cn } from '~/utils/cn';
import { formatDate, formatWeekdayDate, getYear } from '~/utils/date';

import { useEnrichmentContext } from '../context/enrichment-context';
import type { EnrichmentFilters } from '../types/enrichment-context.types';
import { groupAttributes, runSetupOf, SOURCE_LOCALE } from '../utils/run-setup';

import { BatchInfoCostSection } from './batch-info-cost-section';
import { CollapsibleSectionCard } from './collapsible-section-card';

interface Props {
  /** Extra classes for the trigger. */
  className?: string;
  /** Trigger style: the default full-width `card`, or a compact `icon` button
   *  for the collapsed agent-panel rail. */
  triggerVariant?: 'card' | 'icon';
}

/**
 * "Batch Info" sheet for the scheduled content-enrichment funnel — the
 * counterpart to the experiment flow's {@link ExperimentDetailsSheet}. Resolves
 * the batch id and applied filters from the injected {@link EnrichmentContext}
 * and reads the light phase-1 header (the sheet only surfaces header fields),
 * then hands off to the context-free {@link BatchInfoSheetView}. Renders nothing
 * when no batch id is available (e.g. the experiment flow, which has its own sheet).
 */
export function BatchInfoSheet({ className, triggerVariant = 'card' }: Props) {
  const context = useEnrichmentContext();
  // The catalog-ops batch UUID the `jobs/{id}` endpoint expects — NOT
  // `scheduledJobId` (the `scheduled-job-…` slug / workflow id), which 400s.
  const batchId = context.scheduledFlow?.catalogBatchId ?? '';
  const { data, isLoading, isError } = useBatchHeader(batchId, { enabled: !!batchId });

  // No batch id → nothing to show. Guards the experiment flow (which uses its own
  // ExperimentDetailsSheet) and the pre-resolution window.
  if (!batchId) return null;

  return (
    <BatchInfoSheetView
      batchId={batchId}
      data={data}
      isLoading={isLoading}
      isError={isError}
      filters={context.filters}
      triggerVariant={triggerVariant}
      {...(className ? { className } : {})}
    />
  );
}

interface ViewProps {
  /** Batch id used for the title fallback and the copyable id row. On the
   *  detail page this is the human `BatchHeader.batchId`, in the funnel the
   *  catalog-ops job UUID — either reads fine as a label. */
  batchId: string;
  /** Resolved batch header (a full `BatchDetail` is assignable). `undefined`
   *  while loading or on error. */
  data: BatchHeader | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Applied targeting filters, or `{}` where the surface has none (the batch-detail and
   *  feed-audit pages pass the header's stored `filters`, absent on older backends). */
  filters: EnrichmentFilters;
  className?: string;
  triggerVariant?: 'card' | 'icon';
}

/**
 * Presentational "Batch Info" sheet — trigger + drawer + structured body, with
 * no dependency on {@link EnrichmentContext} or any data fetch. Fed explicit
 * props so it can be reused wherever a `BatchHeader` is already in hand: the
 * content-enrichment funnel (via {@link BatchInfoSheet}) and the
 * catalog-operations batch-detail page (from its `useBatchHeader` result,
 * avoiding a second aggregate fetch).
 */
export function BatchInfoSheetView({
  batchId,
  data,
  isLoading,
  isError,
  filters,
  className,
  triggerVariant = 'card',
}: ViewProps) {
  const { t } = useTranslation('content-enrichment');
  const na = t('batchInfo.notAvailable');
  // Prefer the human-readable name; fall back to the batch id when unset.
  const title = data?.name.trim() ? data.name.trim() : batchId;

  return (
    <Sheet>
      <SheetTrigger asChild>
        {triggerVariant === 'icon' ? (
          <Button
            data-testid="enrichment-layout-batch-info-icon-button"
            variant="outline"
            size="icon"
            aria-label={t('batchInfo.trigger')}
            title={t('batchInfo.trigger')}
            className={cn('h-9 w-9 shrink-0 cursor-pointer', className)}
          >
            <Info aria-hidden="true" className="text-accent-main h-4 w-4" />
          </Button>
        ) : (
          <Button
            data-testid="enrichment-layout-batch-info-card-button"
            variant="ghost"
            aria-label={t('batchInfo.trigger')}
            className={cn(
              'bg-accent-100/50 border-accent-main/20 hover:bg-accent-100/70 h-auto w-full cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 transition-all hover:shadow-xs',
              className,
            )}
          >
            <div className="bg-card flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border">
              <Info aria-hidden="true" className="text-accent-main h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p
                data-testid="enrichment-layout-batch-info-card-name"
                className="text-foreground truncate text-sm font-bold"
              >
                {title}
              </p>
              <p className="text-muted-foreground text-xs">{t('batchInfo.batchLabel')}</p>
            </div>
            <ChevronRight aria-hidden="true" className="text-accent-main h-5 w-5 shrink-0" />
          </Button>
        )}
      </SheetTrigger>

      <SheetContent
        data-testid="enrichment-layout-batch-info-sheet"
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-lg"
      >
        {/* Header stays put (the sheet's own close sits in it); only the body scrolls. */}
        <SheetHeader className="bg-background flex-row items-center gap-2.5 space-y-0 border-b px-6 py-4 pr-14 text-left">
          <Info aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
          <SheetTitle data-testid="enrichment-layout-batch-info-title" className="text-base">
            {t('batchInfo.title')}
          </SheetTitle>
        </SheetHeader>

        <div className="bg-muted/30 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          {isLoading ? (
            <DetailsSkeleton />
          ) : isError || !data ? (
            <p
              data-testid="enrichment-layout-batch-info-error"
              className="text-muted-foreground py-6 text-sm"
            >
              {t('batchInfo.loadError')}
            </p>
          ) : (
            <BatchInfoBody data={data} title={title} filters={filters} na={na} t={t} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Structured body of the sheet, split out so the loading/error branches above stay flat. */
function BatchInfoBody({
  data,
  title,
  filters,
  na,
  t,
}: {
  data: BatchHeader;
  title: string;
  filters: EnrichmentFilters;
  na: string;
  t: TFunction;
}) {
  const { t: tc } = useTranslation('common');
  // "Approved · ana@…": the action's label, when it has one, ahead of the actor.
  const lastActionKey = lastActionLabelKey(data.lastActionType);
  const attributes = data.runConfig?.contentAttributes ?? [];
  const productCount = data.productsCuratedTotal ?? null;
  // Collapsed "Products by group" header: the breakdown's own Total row.
  const groupsTotal = productClassesTotal(data.productClassesIncluded);
  const filterEntries = Object.entries(filters).filter(
    ([, v]) => v !== '' && !(Array.isArray(v) && v.length === 0),
  );
  const stage = data.currentStage ? formatStageCode(data.currentStage) : null;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Hero: identity, status, one-line meta, segmented stats ── */}
      <section className="bg-card rounded-xl border">
        <div className="flex items-start gap-3 p-4">
          <PlatformTile platform={data.sourcePlatform} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p
                  data-testid="enrichment-layout-batch-info-platform"
                  className="text-muted-foreground truncate text-xs font-medium"
                >
                  {data.sourcePlatform || na}
                </p>
                <p
                  data-testid="enrichment-layout-batch-info-name"
                  className="text-foreground text-lg leading-snug font-semibold tracking-tight break-words"
                >
                  {title}
                </p>
              </div>
              <StatusChip
                status={data.status}
                className="h-6 shrink-0 rounded-md px-2 text-xs"
                testId="enrichment-layout-batch-info-status-badge"
              />
            </div>
            <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
              <span>{data.source || na}</span>
              {stage && <MetaDot />}
              {stage && <span>{stage}</span>}
              {data.createdAt && <MetaDot />}
              {data.createdAt && (
                <span>{t('batchInfo.meta.created', { date: shortDate(data.createdAt) })}</span>
              )}
              {data.createdAt && data.createdBy && (
                <span className="inline-flex items-center gap-1">
                  {t('batchInfo.meta.by')}
                  <ActorStringLabel
                    value={data.createdBy}
                    showAvatar={false}
                    fallback={na}
                    className="text-foreground text-xs"
                    testId="enrichment-layout-batch-info-meta-created-by-actor"
                  />
                </span>
              )}
            </p>
          </div>
        </div>

        <dl className="divide-border grid grid-cols-3 divide-x border-t">
          <StatCell
            value={productCount != null ? productCount.toLocaleString() : na}
            label={t('batchInfo.fields.products')}
            testId="enrichment-layout-batch-info-stat-products"
          />
          <StatCell
            value={String(attributes.length)}
            label={t('batchInfo.fields.attributes')}
            testId="enrichment-layout-batch-info-stat-attributes"
          />
          {data.useCase === 'META_ADS' ? (
            <StatCell
              value={String(filterEntries.length)}
              label={t('batchInfo.fields.filters')}
              testId="enrichment-layout-batch-info-stat-filters"
            />
          ) : (
            <StatCell
              value={String(runSetupOf(data).locales.length)}
              label={t('batchInfo.fields.languagesStat')}
              testId="enrichment-layout-batch-info-stat-languages"
            />
          )}
        </dl>
      </section>

      <RunSetupSections data={data} t={t} />

      {/* ── Products by group — what the create screen offered, what curation took in ── */}
      {(data.productClassesOffered ?? data.productClassesIncluded) && (
        <CollapsibleSectionCard
          icon={Layers}
          title={t('productGroups.breakdown.title')}
          summary={groupsTotal === null ? na : t('batchInfo.groupsTotal', { count: groupsTotal })}
          testId="enrichment-layout-batch-info-groups"
        >
          <ProductClassesBreakdown
            testId="enrichment-layout-batch-info-product-classes"
            included={data.productClassesIncluded}
            offered={data.productClassesOffered ?? null}
          />
        </CollapsibleSectionCard>
      )}

      {/* ── Cost — lily.ai / cost approvers only; customers fire no request ── */}
      <BatchInfoCostSection data={data} />

      {/* ── Content — grouped for reading; the wire keys are in the tooltip ── */}
      <SectionCard
        icon={FileText}
        title={t('batchInfo.sections.content')}
        aside={t('batchInfo.fields.attributeCount', { count: attributes.length })}
      >
        {attributes.length > 0 ? (
          <div className="space-y-4">
            {groupAttributes(attributes).map((g) => (
              <div key={g.group}>
                <p className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-medium">
                  {t(`batchInfo.groups.${g.group}`)}
                  <span className="text-muted-foreground/70 tabular-nums">({g.keys.length})</span>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {g.keys.map((key, i) => (
                    <span
                      key={key}
                      data-testid={`enrichment-layout-batch-info-attribute-${key}`}
                      title={key}
                      className="bg-muted text-foreground rounded-md px-2 py-1 text-xs leading-none"
                    >
                      {g.labels[i]}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{na}</p>
        )}
      </SectionCard>

      {/* ── Run ── */}
      <SectionCard icon={Activity} title={t('batchInfo.sections.run')}>
        <dl className="divide-border -my-2 divide-y">
          <RunRow
            icon={Plug}
            label={t('batchInfo.fields.source')}
            testId="enrichment-layout-batch-info-run-source"
          >
            {data.sourcePlatform || <Empty na={na} />}
          </RunRow>
          <RunRow
            icon={Briefcase}
            label={t('batchInfo.fields.useCase')}
            testId="enrichment-layout-batch-info-run-use-case"
          >
            {data.source || <Empty na={na} />}
          </RunRow>
          <RunRow
            icon={GitBranch}
            label={t('batchInfo.fields.currentStage')}
            testId="enrichment-layout-batch-info-run-current-stage"
          >
            {stage ?? <Empty na={na} />}
          </RunRow>
          <RunRow
            icon={CalendarDays}
            label={t('batchInfo.fields.createdAt')}
            testId="enrichment-layout-batch-info-run-created-at"
          >
            {data.createdAt ? formatDate(data.createdAt) : <Empty na={na} />}
          </RunRow>
          <RunRow
            icon={User}
            label={t('batchInfo.fields.createdBy')}
            testId="enrichment-layout-batch-info-run-created-by"
          >
            <ActorStringLabel
              value={data.createdBy}
              showAvatar={false}
              fallback={<Empty na={na} />}
              className="justify-end"
              testId="enrichment-layout-batch-info-run-created-by-actor"
            />
          </RunRow>
          <RunRow
            icon={History}
            label={t('batchInfo.fields.lastActionBy')}
            testId="enrichment-layout-batch-info-run-last-action"
          >
            <span className="flex flex-wrap items-center justify-end gap-1.5">
              {lastActionKey && (
                <span data-testid="enrichment-layout-batch-info-run-last-action-type">
                  {tc(lastActionKey)}
                  {data.lastActionBy ? ' ·' : ''}
                </span>
              )}
              <ActorStringLabel
                value={data.lastActionBy}
                showAvatar={false}
                fallback={<Empty na={na} />}
                testId="enrichment-layout-batch-info-run-last-action-actor"
              />
              {data.lastActionAt && (
                <span className="text-muted-foreground text-xs font-normal">
                  · {formatDate(data.lastActionAt)}
                </span>
              )}
            </span>
          </RunRow>
        </dl>
      </SectionCard>

      {/* ── Targeting ── */}
      <SectionCard icon={Target} title={t('batchInfo.sections.targeting')}>
        {filterEntries.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {filterEntries.map(([key, value]) => (
              <span
                key={key}
                data-testid={`enrichment-layout-batch-info-filter-${key}`}
                className="bg-muted flex items-baseline gap-1.5 rounded-md px-2 py-1"
              >
                <span className="text-muted-foreground font-mono text-[11px]">{key}</span>
                <span className="text-foreground text-xs font-medium">
                  {Array.isArray(value) ? value.join(', ') : value}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <p
            data-testid="enrichment-layout-batch-info-filters-empty"
            className="text-muted-foreground flex items-center gap-2 text-sm"
          >
            <FilterX aria-hidden="true" className="h-4 w-4 shrink-0" />
            {t('batchInfo.noFilters')}
          </p>
        )}
      </SectionCard>

      <CopyableId id={data.batchId} t={t} />
    </div>
  );
}

/**
 * Languages, prompt and steps — the full detail behind the side panel's Run setup section,
 * from the same {@link runSetupOf} derivation so the panel and this sheet agree. Languages
 * carry per-locale translation counts once the run has translated (internal users only,
 * like the rest of translation); the prompt row is internal-only, like the retailer picker.
 */
function RunSetupSections({ data, t }: { data: BatchHeader; t: TFunction }) {
  const canTranslate = useCanTranslateContent();
  const canSeePrompt = useCanChooseRetailerPrompt();
  const isMeta = data.useCase === 'META_ADS';
  const setup = runSetupOf(data);
  const summary = useRunTranslations(data.workflowId ?? '', {
    enabled: canTranslate && !isMeta && setup.locales.length > 1,
  });
  const localeState = (code: string): { text: string; tone: 'source' | 'done' | 'pending' } => {
    if (code === SOURCE_LOCALE) return { text: t('runSetup.source'), tone: 'source' };
    const option = toOptions(summary.data ?? []).find(
      (o) => o.locale.toLowerCase() === code.toLowerCase(),
    );
    if (!option) return { text: t('batchInfo.fields.pending'), tone: 'pending' };
    const pass = option.pass + option.flag;
    const held = option.heldBack;
    return {
      text:
        held > 0
          ? t('batchInfo.fields.localeHeld', { pass, held })
          : t('batchInfo.fields.localeDone', { pass }),
      tone: 'done',
    };
  };
  const steps: { key: string; label: string; on: boolean }[] = [
    { key: 'sd', label: t('runSetup.searchDescriptors'), on: setup.searchDescriptors },
    { key: 'pd', label: t('runSetup.phraseDescriptions'), on: setup.phraseDescriptions },
    { key: 'pre', label: t('batchInfo.fields.preScoring'), on: setup.preScoring },
    { key: 'post', label: t('batchInfo.fields.postScoring'), on: setup.postScoring },
  ];
  const scoringOff = !setup.preScoring && !setup.postScoring;

  return (
    <SectionCard icon={Settings2} title={t('batchInfo.sections.setup')}>
      <dl className="space-y-4 text-sm">
        {!isMeta && (
          <SetupRow label={t('runSetup.languages')}>
            <div className="flex flex-wrap gap-1.5">
              {setup.locales.map((code) => {
                const state = localeState(code);
                return (
                  <span
                    key={code}
                    data-testid={`enrichment-layout-batch-info-language-${code}`}
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs',
                      state.tone === 'source' && 'bg-muted text-foreground border-transparent',
                      state.tone === 'done' && 'bg-success-50 text-success border-transparent',
                      state.tone === 'pending' && 'text-muted-foreground border-dashed',
                    )}
                  >
                    <span className="font-medium">{code}</span> · {state.text}
                  </span>
                );
              })}
            </div>
          </SetupRow>
        )}
        {!isMeta && canSeePrompt && (
          <SetupRow label={t('runSetup.prompt')}>
            <span data-testid="enrichment-layout-batch-info-prompt" className="text-foreground">
              {setup.retailer ?? t('runSetup.sharedPrompt')}
            </span>
          </SetupRow>
        )}
        <SetupRow label={t('runSetup.steps')}>
          <ul className="space-y-1.5">
            {steps.map((step) => (
              <li
                key={step.key}
                data-testid={`enrichment-layout-batch-info-step-${step.key}`}
                className="flex items-center gap-2 text-sm"
              >
                {step.on ? (
                  <Check aria-hidden="true" className="text-success h-4 w-4 shrink-0" />
                ) : (
                  <Circle
                    aria-hidden="true"
                    className="text-muted-foreground/60 h-4 w-4 shrink-0"
                  />
                )}
                <span className={step.on ? 'text-foreground' : 'text-muted-foreground'}>
                  {step.label}
                </span>
                <span className="sr-only">
                  {step.on ? t('batchInfo.stepOn') : t('batchInfo.stepOff')}
                </span>
              </li>
            ))}
          </ul>
        </SetupRow>
      </dl>
      {scoringOff && (
        <p
          data-testid="enrichment-layout-batch-info-scoring-off-banner"
          className="bg-warning-50 text-warning mt-4 flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
        >
          <TriangleAlert aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{t('batchInfo.fields.scoringOffNote')}</span>
        </p>
      )}
    </SectionCard>
  );
}

// ─── Primitives ──────────────────────────────────────────────────────────────

/** Short creation date for the hero meta line; the year only shows when it isn't this year. */
function shortDate(iso: string): string {
  const notThisYear = getYear(iso) !== new Date().getFullYear();
  return formatWeekdayDate(iso, notThisYear);
}

/** Middle-dot separator for the hero meta line. */
function MetaDot() {
  return (
    <span aria-hidden="true" className="text-muted-foreground/60">
      ·
    </span>
  );
}

/** Source-platform logo tile; falls back to a generic plug mark for unmapped platforms. */
function PlatformTile({ platform }: { platform: string }) {
  const logo = PLATFORM_LOGO[platform];
  return (
    <div
      title={platform || undefined}
      className="bg-background flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border"
    >
      {logo ? (
        <img src={logo} alt={platform} className="h-5 w-5 object-contain" />
      ) : (
        <Plug aria-hidden="true" className="text-muted-foreground h-4 w-4" />
      )}
    </div>
  );
}

/** One cell of the hero's segmented stat row: big tabular number over a small muted label. */
function StatCell({ value, label, testId }: { value: string; label: string; testId?: string }) {
  return (
    <div data-testid={testId} className="flex min-w-0 flex-col-reverse px-4 py-3">
      <dt className="text-muted-foreground mt-1 truncate text-[11px] font-medium tracking-wide uppercase">
        {label}
      </dt>
      <dd
        data-testid={testId ? `${testId}-value` : undefined}
        className="text-foreground truncate text-xl leading-none font-semibold tracking-tight tabular-nums"
      >
        {value}
      </dd>
    </div>
  );
}

/** Section card with an icon + title header (and optional trailing note) over a padded body. */
function SectionCard({
  icon: Icon,
  title,
  aside,
  children,
}: {
  icon: LucideIcon;
  title: string;
  aside?: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-card rounded-xl border">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <Icon aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
        {aside && <span className="text-muted-foreground ml-auto text-xs">{aside}</span>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Setup definition row: muted label column on the left, value on the right. */
function SetupRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[104px_1fr] items-start gap-4">
      <dt className="text-muted-foreground pt-0.5 text-xs">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** Run definition row: icon + muted label on the left, right-aligned value. */
function RunRow({
  icon: Icon,
  label,
  children,
  testId,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  /** Automation id for the row; the value cell gets `${testId}-value`. */
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="flex items-center justify-between gap-4 py-2.5">
      <dt className="text-muted-foreground flex shrink-0 items-center gap-2 text-sm">
        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
        {label}
      </dt>
      <dd
        data-testid={testId ? `${testId}-value` : undefined}
        className="text-foreground min-w-0 text-right text-sm font-medium"
      >
        {children}
      </dd>
    </div>
  );
}

/** Muted placeholder for an empty value. */
function Empty({ na }: { na: string }) {
  return <span className="text-muted-foreground font-normal">{na}</span>;
}

/**
 * Monospace Batch ID field with copy (check flip + toast). Truncates in the middle — the
 * head ellipsizes while the last 8 chars stay — and the full id is on the `title`.
 */
function CopyableId({ id, t }: { id: string; t: TFunction }) {
  const tail = id.length > 16 ? id.slice(-8) : '';
  const head = tail ? id.slice(0, -8) : id;

  return (
    <div className="bg-card rounded-xl border px-4 py-3">
      <p className="text-muted-foreground mb-1.5 text-xs font-medium">{t('batchInfo.fields.id')}</p>
      <div className="bg-muted/50 flex items-center gap-2 rounded-lg border px-3 py-1.5">
        <span
          data-testid="enrichment-layout-batch-info-batch-id"
          title={id}
          className="text-foreground flex min-w-0 flex-1 font-mono text-xs"
        >
          <span className="truncate">{head}</span>
          {tail && <span className="shrink-0">{tail}</span>}
        </span>
        <CopyIconButton
          testId="enrichment-layout-batch-info-batch-id-copy-button"
          text={id}
          label={t('batchInfo.copyId')}
          copyMessage={t('batchInfo.copied')}
          className="text-muted-foreground hover:text-foreground size-7"
        />
      </div>
    </div>
  );
}

/** Placeholder layout shown while the batch-detail query is in flight. */
function DetailsSkeleton() {
  return (
    <div data-testid="enrichment-layout-batch-info-loading" className="flex flex-col gap-4">
      <Skeleton className="h-36 w-full rounded-xl" />
      <Skeleton className="h-44 w-full rounded-xl" />
      <Skeleton className="h-32 w-full rounded-xl" />
    </div>
  );
}
